/**
 * Route to Documentation Mapping
 *
 * Maps dashboard routes to their corresponding documentation files.
 * Handles dynamic routes like [id], [...path], etc.
 * Supports query parameter-based routing for tab-specific documentation.
 */

export interface RouteDocInfo {
  docPath: string;
  title: string;
  description: string;
}

/**
 * Parse route and query parameters from a full path
 * Example: "/agents/create?tab=canvas" -> { path: "/agents/create", params: { tab: "canvas" } }
 */
export function parseRouteWithParams(fullRoute: string): { path: string; params: Record<string, string> } {
  const [path, queryString] = fullRoute.split('?');
  const params: Record<string, string> = {};

  if (queryString) {
    const searchParams = new URLSearchParams(queryString);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
  }

  return { path, params };
}

// Static route mappings
const staticRoutes: Record<string, RouteDocInfo> = {
  '/overview': {
    docPath: 'overview',
    title: 'Dashboard Overview',
    description: 'Your command center for monitoring and managing the Matricks platform',
  },
  '/agents': {
    docPath: 'agents/index',
    title: 'AI Agents',
    description: 'Create and manage AI agents for automation and assistance',
  },
  '/agents/create': {
    docPath: 'agents/create/index',
    title: 'Create Agent',
    description: 'Create a new AI agent with custom configuration',
  },
  // Agent Create - Tab-specific documentation (path-based routes)
  '/agents/create/form': {
    docPath: 'agents/create/form',
    title: 'Form Builder',
    description: 'Step-by-step wizard to create agents',
  },
  '/agents/create/canvas': {
    docPath: 'agents/create/canvas',
    title: 'Canvas Builder',
    description: 'Visual workflow builder with drag-and-drop nodes',
  },
  '/agents/create/ai': {
    docPath: 'agents/create/ai',
    title: 'AI Prompt Builder',
    description: 'Describe your agent and let AI generate it',
  },
  '/agents/create/json': {
    docPath: 'agents/create/json',
    title: 'JSON Builder',
    description: 'Advanced JSON-based agent configuration',
  },
  '/agents/create/templates': {
    docPath: 'agents/create/templates',
    title: 'Template Gallery',
    description: 'Start from pre-built agent templates',
  },
  // Node type documentation
  '/agents/create/nodes': {
    docPath: 'agents/create/nodes/index',
    title: 'Canvas Nodes',
    description: 'Reference for all canvas node types',
  },
  '/agents/create/nodes/input': {
    docPath: 'agents/create/nodes/input',
    title: 'Input Node',
    description: 'Entry point for workflow data',
  },
  '/agents/create/nodes/output': {
    docPath: 'agents/create/nodes/output',
    title: 'Output Node',
    description: 'Return workflow results',
  },
  '/agents/create/nodes/llm': {
    docPath: 'agents/create/nodes/llm',
    title: 'LLM Node',
    description: 'Call language models for text generation',
  },
  '/agents/create/nodes/tool': {
    docPath: 'agents/create/nodes/tool',
    title: 'Tool Node',
    description: 'Execute MCP tools',
  },
  '/agents/create/nodes/agent': {
    docPath: 'agents/create/nodes/agent',
    title: 'Agent Node',
    description: 'Invoke another agent as a sub-agent',
  },
  '/agents/create/nodes/condition': {
    docPath: 'agents/create/nodes/condition',
    title: 'Condition Node',
    description: 'Branch workflow based on conditions',
  },
  '/agents/create/nodes/loop': {
    docPath: 'agents/create/nodes/loop',
    title: 'Loop Node',
    description: 'Iterate over data collections',
  },
  '/agents/create/nodes/router': {
    docPath: 'agents/create/nodes/router',
    title: 'Router Node',
    description: 'Route to multiple paths based on logic',
  },
  '/agents/create/nodes/parallel': {
    docPath: 'agents/create/nodes/parallel',
    title: 'Parallel Node',
    description: 'Execute branches concurrently',
  },
  '/agents/create/nodes/merge': {
    docPath: 'agents/create/nodes/merge',
    title: 'Merge Node',
    description: 'Combine parallel execution results',
  },
  '/agents/create/nodes/memory': {
    docPath: 'agents/create/nodes/memory',
    title: 'Memory Node',
    description: 'Store and retrieve conversation context',
  },
  '/agents/create/nodes/delay': {
    docPath: 'agents/create/nodes/delay',
    title: 'Delay Node',
    description: 'Pause workflow execution',
  },
  '/agents/create/nodes/transform': {
    docPath: 'agents/create/nodes/transform',
    title: 'Transform Node',
    description: 'Transform data between formats',
  },
  '/agents/create/nodes/validator': {
    docPath: 'agents/create/nodes/validator',
    title: 'Validator Node',
    description: 'Validate data against schemas',
  },
  '/agents/create/nodes/retriever': {
    docPath: 'agents/create/nodes/retriever',
    title: 'Retriever Node',
    description: 'Retrieve documents from vector stores',
  },
  '/agents/create/nodes/orchestrator': {
    docPath: 'agents/create/nodes/orchestrator',
    title: 'Orchestrator Node',
    description: 'Coordinate multi-agent execution',
  },
  '/agents/create/nodes/tool-executor': {
    docPath: 'agents/create/nodes/tool-executor',
    title: 'Tool Executor Node',
    description: 'Execute tools with custom logic',
  },
  '/agents/create/nodes/webhook': {
    docPath: 'agents/create/nodes/webhook',
    title: 'Webhook Node',
    description: 'Trigger workflow from HTTP requests',
  },
  '/agents/create/nodes/schedule': {
    docPath: 'agents/create/nodes/schedule',
    title: 'Schedule Node',
    description: 'Trigger workflow on a schedule',
  },
  '/agents/create/nodes/error-handler': {
    docPath: 'agents/create/nodes/error-handler',
    title: 'Error Handler Node',
    description: 'Handle errors in workflow execution',
  },
  '/agents/create/nodes/note': {
    docPath: 'agents/create/nodes/note',
    title: 'Note Node',
    description: 'Add annotations to your canvas',
  },
  // New Phase 1-4 node types
  '/agents/create/nodes/workflow': {
    docPath: 'agents/create/nodes/workflow',
    title: 'Workflow Node',
    description: 'Embed sub-workflows for modular design',
  },
  '/agents/create/nodes/event-trigger': {
    docPath: 'agents/create/nodes/event-trigger',
    title: 'Event Trigger Node',
    description: 'Trigger workflows from system events',
  },
  '/agents/create/nodes/approval': {
    docPath: 'agents/create/nodes/approval',
    title: 'Approval Node',
    description: 'Human-in-the-loop approval gates',
  },
  '/agents/create/nodes/batch-start': {
    docPath: 'agents/create/nodes/batch-start',
    title: 'Batch Start Node',
    description: 'Initiate batch processing of items',
  },
  '/agents/create/nodes/batch-end': {
    docPath: 'agents/create/nodes/batch-end',
    title: 'Batch End Node',
    description: 'Collect and aggregate batch results',
  },
  '/agents/create/nodes/rate-limit': {
    docPath: 'agents/create/nodes/rate-limit',
    title: 'Rate Limit Node',
    description: 'Control request throughput',
  },
  '/agents/create/nodes/retry-policy': {
    docPath: 'agents/create/nodes/retry-policy',
    title: 'Retry Policy Node',
    description: 'Configure automatic retry behavior',
  },
  '/agents/create/nodes/rag-query': {
    docPath: 'agents/create/nodes/rag-query',
    title: 'RAG Query Node',
    description: 'Retrieve documents with vector search',
  },
  '/agents/create/nodes/memory-read': {
    docPath: 'agents/create/nodes/memory-read',
    title: 'Memory Read Node',
    description: 'Read from agent memory',
  },
  '/agents/create/nodes/memory-write': {
    docPath: 'agents/create/nodes/memory-write',
    title: 'Memory Write Node',
    description: 'Write to agent memory',
  },
  '/agents/create/nodes/vision': {
    docPath: 'agents/create/nodes/vision',
    title: 'Vision Node',
    description: 'Analyze images with AI',
  },
  '/agents/create/nodes/transcribe': {
    docPath: 'agents/create/nodes/transcribe',
    title: 'Transcribe Node',
    description: 'Convert audio/video to text',
  },
  '/agents/create/nodes/evaluate': {
    docPath: 'agents/create/nodes/evaluate',
    title: 'Evaluate Node',
    description: 'Evaluate outputs against criteria',
  },
  // Use case tutorials
  '/agents/create/use-cases': {
    docPath: 'agents/create/use-cases/index',
    title: 'Canvas Use Cases',
    description: 'Step-by-step tutorials for building common agent workflows',
  },
  // Chatbot & RAG tutorials
  '/agents/create/use-cases/chatbot-rag': {
    docPath: 'agents/create/use-cases/chatbot-rag/index',
    title: 'Chatbot & RAG Tutorials',
    description: 'Build conversational agents and knowledge-based assistants',
  },
  '/agents/create/use-cases/chatbot-rag/simple-chat-bot': {
    docPath: 'agents/create/use-cases/chatbot-rag/simple-chat-bot',
    title: 'Simple Chat Bot',
    description: 'Build a basic conversational agent in 5 minutes',
  },
  '/agents/create/use-cases/chatbot-rag/faq-knowledge-base': {
    docPath: 'agents/create/use-cases/chatbot-rag/faq-knowledge-base',
    title: 'FAQ Knowledge Base',
    description: 'Build a Q&A bot with document retrieval and RAG',
  },
  '/agents/create/use-cases/chatbot-rag/customer-support-escalation': {
    docPath: 'agents/create/use-cases/chatbot-rag/customer-support-escalation',
    title: 'Customer Support with Escalation',
    description: 'Build a full support system with intent routing and escalation',
  },
  // Automation & Tools tutorials
  '/agents/create/use-cases/automation-tools': {
    docPath: 'agents/create/use-cases/automation-tools/index',
    title: 'Automation & Tools Tutorials',
    description: 'Build automated workflows with webhooks and scheduled tasks',
  },
  '/agents/create/use-cases/automation-tools/slack-notification-bot': {
    docPath: 'agents/create/use-cases/automation-tools/slack-notification-bot',
    title: 'Slack Notification Bot',
    description: 'Build a webhook-triggered notification system',
  },
  '/agents/create/use-cases/automation-tools/daily-report-generator': {
    docPath: 'agents/create/use-cases/automation-tools/daily-report-generator',
    title: 'Daily Report Generator',
    description: 'Build a scheduled agent for automated reporting',
  },
  '/agents/create/use-cases/automation-tools/data-pipeline-integration': {
    docPath: 'agents/create/use-cases/automation-tools/data-pipeline-integration',
    title: 'Data Pipeline Integration',
    description: 'Build a multi-source data processing pipeline with validation and routing',
  },
  // Multi-Agent tutorials
  '/agents/create/use-cases/multi-agent': {
    docPath: 'agents/create/use-cases/multi-agent/index',
    title: 'Multi-Agent Tutorials',
    description: 'Build sophisticated multi-agent systems and orchestration',
  },
  '/agents/create/use-cases/multi-agent/research-assistant-delegation': {
    docPath: 'agents/create/use-cases/multi-agent/research-assistant-delegation',
    title: 'Research Assistant Delegation',
    description: 'Build an agent that delegates to specialized sub-agents',
  },
  '/agents/create/use-cases/multi-agent/content-review-pipeline': {
    docPath: 'agents/create/use-cases/multi-agent/content-review-pipeline',
    title: 'Content Review Pipeline',
    description: 'Build a parallel content review system with multiple agents',
  },
  '/agents/create/use-cases/multi-agent/autonomous-project-manager': {
    docPath: 'agents/create/use-cases/multi-agent/autonomous-project-manager',
    title: 'Autonomous Project Manager',
    description: 'Build an orchestrated multi-agent system for project management',
  },
  // Data Sync use cases
  '/agents/create/use-cases/data-sync': {
    docPath: 'agents/create/use-cases/data-sync/index',
    title: 'Data Synchronization',
    description: 'Build agents for data sync and integration',
  },
  '/agents/create/use-cases/data-sync/database-sync': {
    docPath: 'agents/create/use-cases/data-sync/database-sync',
    title: 'Database Synchronization',
    description: 'Sync records between databases with conflict resolution',
  },
  '/agents/create/use-cases/data-sync/api-aggregation': {
    docPath: 'agents/create/use-cases/data-sync/api-aggregation',
    title: 'API Aggregation',
    description: 'Combine data from multiple APIs',
  },
  // Enterprise Security use cases
  '/agents/create/use-cases/enterprise-security': {
    docPath: 'agents/create/use-cases/enterprise-security/index',
    title: 'Enterprise Security',
    description: 'Build agents for compliance and security',
  },
  '/agents/create/use-cases/enterprise-security/compliance-audit': {
    docPath: 'agents/create/use-cases/enterprise-security/compliance-audit',
    title: 'Compliance Audit',
    description: 'Automated compliance checking and reporting',
  },
  // FinOps use cases
  '/agents/create/use-cases/finops': {
    docPath: 'agents/create/use-cases/finops/index',
    title: 'FinOps',
    description: 'Build agents for cloud cost optimization',
  },
  '/agents/create/use-cases/finops/cost-tracking': {
    docPath: 'agents/create/use-cases/finops/cost-tracking',
    title: 'Cost Tracking',
    description: 'Monitor and analyze cloud spending',
  },
  '/integrations': {
    docPath: 'integrations/index',
    title: 'Integrations',
    description: 'Connect external services and data sources',
  },
  '/integrations/add': {
    docPath: 'integrations/add',
    title: 'Add Integration',
    description: 'Connect a new service or data source',
  },
  '/integrations/callback': {
    docPath: 'integrations/callback',
    title: 'Integration Callback',
    description: 'OAuth authentication callback handler',
  },
  '/tools': {
    docPath: 'tools/index',
    title: 'MCP Tools',
    description: 'Browse and manage Model Context Protocol tools',
  },
  '/data': {
    docPath: 'data/index',
    title: 'Data Browser',
    description: 'Explore and query your connected data sources',
  },
  '/crews': {
    docPath: 'crews/index',
    title: 'Agent Crews',
    description: 'Orchestrate multi-agent teams for complex tasks',
  },
  '/crews/create': {
    docPath: 'crews/create',
    title: 'Create Crew',
    description: 'Build a new agent crew for collaborative automation',
  },
  '/api-keys': {
    docPath: 'api-keys',
    title: 'API Keys',
    description: 'Manage your API keys for programmatic access',
  },
  '/profile': {
    docPath: 'profile',
    title: 'Profile',
    description: 'Manage your personal profile and preferences',
  },
  '/settings': {
    docPath: 'settings',
    title: 'Settings',
    description: 'Configure organization settings and preferences',
  },
  '/docs': {
    docPath: 'docs/index',
    title: 'Documentation',
    description: 'Browse all platform documentation by feature and use case',
  },
  '/analytics': {
    docPath: 'analytics',
    title: 'Analytics',
    description: 'View usage analytics and performance metrics',
  },
  // Admin routes
  '/admin': {
    docPath: 'admin/index',
    title: 'Admin Dashboard',
    description: 'Administrative overview and management',
  },
  '/admin/audit': {
    docPath: 'admin/audit',
    title: 'Audit Logs',
    description: 'Review system audit trail and activity logs',
  },
  '/admin/org': {
    docPath: 'admin/org/index',
    title: 'Organization Admin',
    description: 'Manage your organization settings',
  },
  '/admin/org/users': {
    docPath: 'admin/org/users',
    title: 'Team Members',
    description: 'Manage organization users and access',
  },
  '/admin/org/roles': {
    docPath: 'admin/org/roles',
    title: 'Roles',
    description: 'Configure roles and permissions',
  },
  '/admin/org/policies': {
    docPath: 'admin/org/policies',
    title: 'Security Policies',
    description: 'Manage access control policies',
  },
  '/admin/org/api-keys': {
    docPath: 'admin/org/api-keys',
    title: 'Organization API Keys',
    description: 'Manage organization-level API keys',
  },
  '/admin/org/billing': {
    docPath: 'admin/org/billing',
    title: 'Billing',
    description: 'Manage subscription and billing',
  },
  '/admin/org/sso': {
    docPath: 'admin/org/sso',
    title: 'SSO Configuration',
    description: 'Configure Single Sign-On',
  },
  '/admin/org/scim': {
    docPath: 'admin/org/scim',
    title: 'SCIM Provisioning',
    description: 'Configure user provisioning with SCIM',
  },
  '/admin/org/sdk-keys': {
    docPath: 'admin/org/sdk-keys',
    title: 'SDK & CLI Keys',
    description: 'Manage API keys for SDK and CLI',
  },
  '/admin/superadmin': {
    docPath: 'admin/superadmin/index',
    title: 'Platform Admin',
    description: 'Platform-wide administration',
  },
  '/admin/superadmin/users': {
    docPath: 'admin/superadmin/users',
    title: 'All Users',
    description: 'Manage all platform users',
  },
  '/admin/superadmin/organizations': {
    docPath: 'admin/superadmin/organizations',
    title: 'Organizations',
    description: 'Manage all organizations on the platform',
  },
  '/admin/superadmin/system': {
    docPath: 'admin/superadmin/system',
    title: 'System Health',
    description: 'Monitor system health and configuration',
  },
  '/admin/data/postgres': {
    docPath: 'admin/data/postgres',
    title: 'PostgreSQL Viewer',
    description: 'Browse PostgreSQL database',
  },
  '/admin/data/redis': {
    docPath: 'admin/data/redis',
    title: 'Redis Viewer',
    description: 'Browse Redis data store',
  },
  '/admin/data/clickhouse': {
    docPath: 'admin/data/clickhouse',
    title: 'ClickHouse Viewer',
    description: 'Browse ClickHouse analytics database',
  },
  // Environments
  '/environments': {
    docPath: 'environments/index',
    title: 'Environment Management',
    description: 'Manage development, staging, and production environments',
  },
  '/environments/secrets': {
    docPath: 'environments/secrets',
    title: 'Environment Secrets',
    description: 'Manage secrets and environment variables',
  },
  '/environments/deployments': {
    docPath: 'environments/deployments',
    title: 'Deployments',
    description: 'Deploy and promote agents across environments',
  },
  // Connectors
  '/connectors': {
    docPath: 'connectors/index',
    title: 'Custom Connectors',
    description: 'Build custom API connectors',
  },
  '/connectors/builder': {
    docPath: 'connectors/builder',
    title: 'Connector Builder',
    description: 'Build custom API connectors step by step',
  },
  '/connectors/operations': {
    docPath: 'connectors/operations',
    title: 'Connector Operations',
    description: 'Define and configure connector operations',
  },
  '/connectors/testing': {
    docPath: 'connectors/testing',
    title: 'Testing Connectors',
    description: 'Test and validate custom connectors',
  },
  // Marketplace
  '/marketplace': {
    docPath: 'marketplace/index',
    title: 'Template Marketplace',
    description: 'Browse and share agent templates',
  },
  '/marketplace/browse': {
    docPath: 'marketplace/browse',
    title: 'Browse Marketplace',
    description: 'Discover and search for templates',
  },
  '/marketplace/install': {
    docPath: 'marketplace/install',
    title: 'Installing Templates',
    description: 'Install and configure marketplace templates',
  },
  '/marketplace/publish': {
    docPath: 'marketplace/publish',
    title: 'Publishing Templates',
    description: 'Share your agents on the marketplace',
  },
};

// Dynamic route patterns
interface DynamicPattern {
  pattern: RegExp;
  getDocInfo: (match: RegExpMatchArray) => RouteDocInfo;
}

const dynamicPatterns: DynamicPattern[] = [
  // /agents/[id] - Agent detail page
  {
    pattern: /^\/agents\/([^/]+)$/,
    getDocInfo: () => ({
      docPath: 'agents/detail',
      title: 'Agent Details',
      description: 'View and edit agent configuration',
    }),
  },
  // /agents/[id]/chat - Agent chat page
  {
    pattern: /^\/agents\/([^/]+)\/chat$/,
    getDocInfo: () => ({
      docPath: 'agents/chat',
      title: 'Agent Chat',
      description: 'Interactive conversation with your agent',
    }),
  },
  // /integrations/[provider] - Integration detail
  {
    pattern: /^\/integrations\/([^/]+)$/,
    getDocInfo: (match) => ({
      docPath: 'integrations/detail',
      title: `${match[1]} Integration`,
      description: 'Manage integration connections and settings',
    }),
  },
  // /tools/[id] - Tool detail
  {
    pattern: /^\/tools\/([^/]+)$/,
    getDocInfo: () => ({
      docPath: 'tools/detail',
      title: 'Tool Details',
      description: 'View tool specifications and documentation',
    }),
  },
  // /data/[alias] - Data source
  {
    pattern: /^\/data\/([^/]+)$/,
    getDocInfo: () => ({
      docPath: 'data/explorer',
      title: 'Data Explorer',
      description: 'Browse and query data source',
    }),
  },
  // /data/[...path] - Nested data path
  {
    pattern: /^\/data\/(.+)$/,
    getDocInfo: () => ({
      docPath: 'data/explorer',
      title: 'Data Explorer',
      description: 'Browse and query data',
    }),
  },
];

/**
 * Get documentation info for a given route
 * Supports query parameters for tab-specific documentation
 */
export function getDocInfoForRoute(route: string): RouteDocInfo {
  // Parse route and query params
  const { path, params } = parseRouteWithParams(route);

  // Check if there's a tab parameter and try tab-specific route first
  if (params.tab) {
    const tabRoute = `${path}?tab=${params.tab}`;
    if (staticRoutes[tabRoute]) {
      return staticRoutes[tabRoute];
    }
  }

  // Check static routes with full route (including query string)
  if (staticRoutes[route]) {
    return staticRoutes[route];
  }

  // Check static routes with path only (without query string)
  if (staticRoutes[path]) {
    return staticRoutes[path];
  }

  // Check dynamic patterns with path only
  for (const { pattern, getDocInfo } of dynamicPatterns) {
    const match = path.match(pattern);
    if (match) {
      return getDocInfo(match);
    }
  }

  // Fallback to generic help
  return {
    docPath: 'overview',
    title: 'Help',
    description: 'General platform documentation',
  };
}

/**
 * Get all documented routes (for search/index)
 */
export function getAllDocumentedRoutes(): Array<{ route: string } & RouteDocInfo> {
  return Object.entries(staticRoutes).map(([route, info]) => ({
    route,
    ...info,
  }));
}

/**
 * Normalize a route path for documentation lookup
 * Preserves query parameters for tab-specific documentation
 */
export function normalizeRoute(route: string): string {
  const { path, params } = parseRouteWithParams(route);

  // Remove trailing slash from path
  let normalizedPath = path.replace(/\/$/, '');

  // Ensure leading slash
  if (!normalizedPath.startsWith('/')) {
    normalizedPath = '/' + normalizedPath;
  }

  // Reconstruct with query params if present
  if (Object.keys(params).length > 0) {
    const queryString = new URLSearchParams(params).toString();
    return `${normalizedPath}?${queryString}`;
  }

  return normalizedPath;
}
